import { SKILLS, type SkillId } from './catalog.ts';
import { createInitialState } from './state.ts';
import { recordProgress } from './progress.ts';
import type { GameState, PrestigeState } from './types.ts';
import {
  constellationNode, constellationNodeLockReasons, ownsConstellationNode, type ConstellationNodeId
} from './constellation.ts';

export const FIRST_ASCENSION_VALUE = 100_000;

export type PrestigeUpgradeId = keyof PrestigeState['upgrades'];

interface PrestigeUpgradeDefinition {
  baseCost: number;
  growth: number;
  maxRank: number;
  requiredAscensions?: number;
  ascensionsPerRank?: number;
}

const PRESTIGE_UPGRADES: Record<PrestigeUpgradeId, PrestigeUpgradeDefinition> = {
  eternalCraft: { baseCost: 1, growth: 1.55, maxRank: 12 },
  echoingKnowledge: { baseCost: 1, growth: 1.6, maxRank: 12 },
  deepSlumber: { baseCost: 2, growth: 2, maxRank: 6 },
  perfectRecall: { baseCost: 2, growth: 2, maxRank: 5 },
  resonantExchange: { baseCost: 2, growth: 1.7, maxRank: 10 },
  harmonicBond: { baseCost: 3, growth: 2, maxRank: 5 },
  swiftHands: { baseCost: 1, growth: 1.6, maxRank: 10 },
  legacyVault: { baseCost: 1, growth: 1.7, maxRank: 10 },
  twinEchoes: { baseCost: 8, growth: 2, maxRank: 3, requiredAscensions: 5, ascensionsPerRank: 5 },
  beyondTheVeil: { baseCost: 1, growth: 1.2, maxRank: 8, requiredAscensions: 5, ascensionsPerRank: 3 }
};

export const PRESTIGE_UPGRADE_PREREQUISITES: Partial<Record<PrestigeUpgradeId, { id: PrestigeUpgradeId; rank: number }>> = {
  harmonicBond: { id: 'eternalCraft', rank: 3 },
  beyondTheVeil: { id: 'echoingKnowledge', rank: 3 },
  perfectRecall: { id: 'deepSlumber', rank: 2 },
  twinEchoes: { id: 'perfectRecall', rank: 2 },
  legacyVault: { id: 'resonantExchange', rank: 2 },
  swiftHands: { id: 'legacyVault', rank: 1 }
};

export interface PrestigeActionResult {
  ok: boolean;
  state: GameState;
  reason?: string;
  gainedShards?: number;
  cost?: number;
}

export function calculateEchoShards(productionValue: number, milestoneMultiplier = 1): number {
  if (productionValue < FIRST_ASCENSION_VALUE) return 0;
  return Math.floor((productionValue / FIRST_ASCENSION_VALUE) ** 0.55 * Math.max(1, milestoneMultiplier));
}

export function pendingEchoShards(state: GameState): number {
  return calculateEchoShards(state.run.productionValue);
}

export function productionValueForEchoShards(shards: number): number {
  if (shards <= 0) return 0;
  return FIRST_ASCENSION_VALUE * shards ** (1 / 0.55);
}

export function nextEchoShardTarget(state: GameState): number {
  return productionValueForEchoShards(pendingEchoShards(state) + 1);
}

export function prestigeUpgradeCost(state: GameState, upgrade: PrestigeUpgradeId): number {
  const definition = PRESTIGE_UPGRADES[upgrade];
  const rank = state.prestige.upgrades[upgrade] ?? 0;
  if (rank >= definition.maxRank) return Number.POSITIVE_INFINITY;
  return Math.max(1, Math.ceil(definition.baseCost * definition.growth ** rank));
}

export function prestigeUpgradeMaxRank(upgrade: PrestigeUpgradeId): number {
  return PRESTIGE_UPGRADES[upgrade].maxRank;
}

export function prestigeUpgradeAscensionRequirement(state: GameState, upgrade: PrestigeUpgradeId): number {
  const definition = PRESTIGE_UPGRADES[upgrade];
  if (!definition.requiredAscensions) return 0;
  const rank = state.prestige.upgrades[upgrade] ?? 0;
  return definition.requiredAscensions + rank * (definition.ascensionsPerRank ?? 0);
}

export function prestigeUpgradePrerequisite(state: GameState, upgrade: PrestigeUpgradeId): { id: PrestigeUpgradeId; rank: number } | undefined {
  if ((state.prestige.upgrades[upgrade] ?? 0) > 0) return undefined;
  const prerequisite = PRESTIGE_UPGRADE_PREREQUISITES[upgrade];
  if (!prerequisite || (state.prestige.upgrades[prerequisite.id] ?? 0) >= prerequisite.rank) return undefined;
  return prerequisite;
}

export function legacyVaultStartingGold(rank: number): number {
  const safeRank = Math.min(PRESTIGE_UPGRADES.legacyVault.maxRank, Math.max(0, Math.floor(rank)));
  return safeRank === 0 ? 0 : 100 * (2 ** safeRank - 1);
}

export function buyPrestigeUpgrade(state: GameState, upgrade: PrestigeUpgradeId, now = Date.now()): PrestigeActionResult {
  const cost = prestigeUpgradeCost(state, upgrade);
  const requiredAscensions = prestigeUpgradeAscensionRequirement(state, upgrade);
  const prerequisite = prestigeUpgradePrerequisite(state, upgrade);
  if (!Number.isFinite(cost)) return { ok: false, state, reason: 'Upgrade is already at maximum rank.' };
  if (state.prestige.ascensions < requiredAscensions) {
    return { ok: false, state, cost, reason: `Requires ${requiredAscensions} Ascensions.` };
  }
  if (prerequisite) return { ok: false, state, cost, reason: `Requires ${prerequisite.id} rank ${prerequisite.rank}.` };
  if (state.prestige.echoShards < cost) return { ok: false, state, cost, reason: 'Not enough Echo Shards.' };
  return {
    ok: true,
    cost,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: {
        ...state.prestige,
        echoShards: state.prestige.echoShards - cost,
        upgrades: { ...state.prestige.upgrades, [upgrade]: (state.prestige.upgrades[upgrade] ?? 0) + 1 }
      }
    }
  };
}

export function buyConstellationNode(state: GameState, id: ConstellationNodeId, now = Date.now()): PrestigeActionResult {
  state = recordProgress(state).state;
  const node = constellationNode(id);
  if (ownsConstellationNode(state, id)) return { ok: false, state, reason: 'Constellation node is already active.' };
  const reasons = constellationNodeLockReasons(state, id);
  if (reasons.length > 0) return { ok: false, state, cost: node.cost, reason: `Requires ${reasons.join(', ')}.` };
  if (state.prestige.echoShards < node.cost) return { ok: false, state, cost: node.cost, reason: 'Not enough Echo Shards.' };
  return {
    ok: true,
    cost: node.cost,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: {
        ...state.prestige,
        echoShards: state.prestige.echoShards - node.cost,
        constellationNodes: [...(state.prestige.constellationNodes ?? []), id]
      }
    }
  };
}

export function setPassiveSkills(state: GameState, skills: SkillId[], now = Date.now()): PrestigeActionResult {
  if ((state.prestige.upgrades.twinEchoes ?? 0) < 1) {
    return { ok: false, state, reason: 'Twin Echoes must be unlocked first.' };
  }
  const unique = [...new Set(skills)];
  if (unique.length !== skills.length || unique.length > 2 || unique.some((skill) => !SKILLS.includes(skill))) {
    return { ok: false, state, reason: 'Choose up to two different support skills.' };
  }
  return {
    ok: true,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: { ...state.prestige, passiveSkills: unique }
    }
  };
}

export function ascendRealm(state: GameState, now = Date.now()): PrestigeActionResult {
  state = recordProgress(state).state;
  const gainedShards = pendingEchoShards(state);
  if (gainedShards <= 0) return { ok: false, state, reason: 'The realm has not produced enough value yet.' };
  const fresh = createInitialState(state.playerId, now);
  fresh.run.gold = legacyVaultStartingGold(state.prestige.upgrades.legacyVault ?? 0);
  const ascendedState: GameState = {
    ...fresh,
    createdAt: state.createdAt,
    revision: state.revision + 1,
    prestige: {
      ...state.prestige,
      echoShards: state.prestige.echoShards + gainedShards,
      lifetimeEchoShards: state.prestige.lifetimeEchoShards + gainedShards,
      ascensions: state.prestige.ascensions + 1,
      passiveSkills: state.prestige.passiveSkills ?? [],
      milestones: state.prestige.milestones ?? [],
      achievements: state.prestige.achievements ?? [],
      upgrades: { ...fresh.prestige.upgrades, ...state.prestige.upgrades }
    },
    stats: state.stats
  };
  return {
    ok: true,
    gainedShards,
    state: recordProgress(ascendedState).state
  };
}
